import { useEffect, useRef } from "react";
import { useAuth } from "./use-auth";
import { withCsrfHeader, readCsrfCookie } from "@/lib/queryClient";

// ── Shared last-activity registry ──────────────────────────────────────────
// Exported so other modules (e.g. TimecardControl) can read the exact
// last-interaction timestamp without adding duplicate event listeners. The
// timecard and engagement systems must share one interaction source to stay
// semantically aligned.
let _lastActivityMs: number = Date.now();

/** Returns the millisecond timestamp of the most recent genuine user interaction. */
export function getLastActivityMs(): number {
  return _lastActivityMs;
}

const SESSION_STORAGE_KEY = "entertain_crm_session_id";

// Engaged-time model (Task #554). We send an engagement "ping" whenever the
// user genuinely interacts with the app. The server (POST /api/sessions/
// heartbeat) rolls a brand-new session row whenever the gap between two pings
// exceeds the idle window, so each row is one continuous engaged block and the
// dashboard's Active Time = sum of in-window gaps. The client's job is simply
// to ping on real interaction, throttled, and to STOP pinging once the user
// goes idle — so leaving a tab open overnight never accrues Active Time.

// Don't send more than one ping per this interval. Comfortably above the
// server's 30s per-session DB-write rate limit so a flush never collides.
const PING_THROTTLE_MS = 45 * 1000;
// How often the flush timer checks whether new engagement needs a ping.
const FLUSH_INTERVAL_MS = 20 * 1000;
// Mirror of the server ACTIVITY_IDLE_WINDOW_MS. If the user hasn't interacted
// within this window we stop pinging, so idle time is never counted and the
// server rolls a fresh row on their next interaction.
const IDLE_WINDOW_MS = 5 * 60 * 1000;

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

export function useSessionTracker() {
  const { user } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const inFlightRef = useRef<boolean>(false);
  const lastSentRef = useRef<number>(0);

  useEffect(() => {
    if (!user) return;

    const sessionId = getOrCreateSessionId();

    // Standard (cancellable, header-bearing) ping. NOTE: we deliberately do
    // NOT skip when document.hidden — a user interacting after the tab was
    // backgrounded (e.g. alt-tabbing back and clicking) is genuine engagement
    // and must be recorded. Idle suppression is handled purely by the
    // IDLE_WINDOW_MS check below, not by tab visibility.
    const sendHeartbeat = async (force = false) => {
      const now = Date.now();
      // Idle gate: only ping if the user genuinely interacted within the
      // engaged-idle window. This is what prevents an open-but-untouched tab
      // from accruing Active Time.
      if (!force && now - lastActivityRef.current > IDLE_WINDOW_MS) return;
      // Avoid overlapping in-flight requests.
      if (inFlightRef.current) return;
      // Throttle.
      if (!force && now - lastSentRef.current < PING_THROTTLE_MS) return;

      inFlightRef.current = true;
      lastSentRef.current = now;
      try {
        await fetch("/api/sessions/heartbeat", {
          method: "POST",
          headers: await withCsrfHeader({ "Content-Type": "application/json" }),
          credentials: "include",
          body: JSON.stringify({ sessionId }),
        });
      } catch (error) {
        console.error("Failed to send session heartbeat:", error);
      } finally {
        inFlightRef.current = false;
      }
    };

    // Beacon-style final ping for unload / tab-hide so the last sliver of
    // engagement isn't lost when the page goes away. Uses fetch keepalive
    // (not navigator.sendBeacon) because the heartbeat route is NOT CSRF-exempt
    // and sendBeacon cannot set the X-CSRF-Token header. The CSRF token is read
    // synchronously from the cookie (already bootstrapped during the session),
    // so no await is needed inside the unload handler.
    const sendUnloadBeacon = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > IDLE_WINDOW_MS) return;
      if (now - lastSentRef.current < 1000) return; // just sent one
      lastSentRef.current = now;
      const token = readCsrfCookie();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-CSRF-Token"] = token;
      try {
        void fetch("/api/sessions/heartbeat", {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ sessionId }),
          keepalive: true,
        });
      } catch {
        // Best-effort on unload; nothing we can do if it fails.
      }
    };

    // Record genuine interaction. Updates the activity watermark and attempts a
    // throttled ping so a continuously-engaged user keeps one session row alive.
    // Also updates the module-level shared registry so TimecardControl and any
    // other consumer can read the exact last-interaction instant without adding
    // their own duplicate event listeners.
    const recordActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      _lastActivityMs = now; // keep shared registry in sync
      void sendHeartbeat(false);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is being hidden — flush a final engaged ping if recently active.
        sendUnloadBeacon();
      } else {
        // Returning to the tab is itself engagement.
        recordActivity();
      }
    };

    // Curated set of GENUINE interaction signals. Deliberately excludes raw
    // mousemove (passive cursor drift) so idle presence isn't mistaken for work.
    const activityEvents: Array<keyof DocumentEventMap | keyof WindowEventMap> = [
      "mousedown",
      "keydown",
      "scroll",
      "wheel",
      "input",
      "pointerdown",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((evt) => {
      window.addEventListener(evt as string, recordActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", sendUnloadBeacon);

    // Initial ping on mount (force) so a freshly opened session registers.
    lastActivityRef.current = Date.now();
    void sendHeartbeat(true);

    // Flush timer: only re-ping when there has been NEW activity since the last
    // send. This both keeps a long continuous session pinging (so the server
    // never rolls it) AND guarantees we stop once the user goes idle — the
    // watermark stops advancing, so no trailing/idle time is ever counted.
    intervalRef.current = setInterval(() => {
      if (lastActivityRef.current > lastSentRef.current) {
        void sendHeartbeat(false);
      }
    }, FLUSH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt as string, recordActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", sendUnloadBeacon);
    };
  }, [user]);
}
